/**
 * The two account operations an admin had no way to perform: move someone's sign-in address,
 * and delete an account that has been created.
 *
 * Both live here so the Members list and the Practitioners tab drive identical behaviour.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AtSign, Trash2, TriangleAlert, Loader2, ShieldAlert, Info } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Shell = ({ title, icon, onClose, children, testid }) => (
  <div
    className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    onClick={onClose}
    data-testid={testid}
  >
    <div
      className="w-full max-w-lg rounded-2xl border border-white/10 bg-slate-900 shadow-2xl max-h-[88vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-white/10">
        {icon}
        <h3 className="text-white font-medium text-base">{title}</h3>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

/** Move a member's sign-in address. */
export const ChangeEmailDialog = ({ account, onClose, onDone }) => {
  const [next, setNext] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/users/change-email`,
        { user_id: account.id, new_email: next.trim(), notify },
        auth(),
      );
      toast.success(`Signs in with ${r.data.new_email} from now on`);
      if (r.data.signs_in_with_google) {
        toast.warning('They sign in with Google — they must re-link Google to the new address');
      }
      onDone?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change the address');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Change sign-in address"
      icon={<AtSign className="w-5 h-5 text-cyan-300" />}
      onClose={onClose}
      testid="change-email-dialog"
    >
      <p className="text-sm text-gray-300 leading-relaxed">
        The account keeps everything — documents accepted, agreement, profile, history. Only the
        address that signs them in changes, and the old one is freed for anyone to use.
      </p>

      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
        <div className="text-gray-500 text-xs uppercase tracking-wide">Currently signs in with</div>
        <div className="text-white mt-1 break-all" data-testid="change-email-current">
          {account.email}
        </div>
        <div className="text-gray-400 text-xs mt-1">{account.full_name}</div>
      </div>

      <label className="block mt-4 text-sm text-gray-300">New address</label>
      <Input
        value={next}
        onChange={(e) => setNext(e.target.value)}
        placeholder="name@example.com"
        className="mt-1.5 bg-slate-800 border-white/10 text-white placeholder:text-gray-500"
        data-testid="change-email-input"
      />

      <label className="flex items-center gap-2.5 mt-4 text-sm text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
          className="w-4 h-4 accent-cyan-500"
          data-testid="change-email-notify"
        />
        Tell both addresses — the old one that it no longer signs them in, the new one that it does
      </label>

      {account.signs_in_with_google && (
        <div className="mt-4 flex gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-3.5">
          <Info className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-100/90 leading-relaxed">
            This person signs in with Google. Changing the address here does not change which
            Google account they click — they will need to re-link Google to the new address.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={onClose} className="text-gray-300 hover:text-white">
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={busy || !next.trim()}
          className="bg-cyan-600 hover:bg-cyan-500 text-white"
          data-testid="change-email-confirm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change address'}
        </Button>
      </div>
    </Shell>
  );
};

/** Delete an account that has been created. Shows what goes, refuses if it owns anything. */
export const RemoveAccountDialog = ({ account, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [reinvite, setReinvite] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users/${account.id}/removal-preview`, auth());
      setPreview(r.data);
      setReinvite(r.data.from_invite);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not read this account');
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/users/remove`,
        { user_id: account.id, reinvite, send_invite_email: true },
        auth(),
      );
      toast.success(`${account.email} removed — ${r.data.total} records`);
      if (r.data.reinvite?.ok) {
        toast.success(r.data.reinvite.emailed
          ? 'A fresh invite is on its way'
          : 'Fresh invite created — email did not send, use Copy link in Practitioners');
      }
      onDone?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove this account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Delete this account"
      icon={<Trash2 className="w-5 h-5 text-rose-300" />}
      onClose={onClose}
      testid="remove-account-dialog"
    >
      {error && (
        <p className="text-sm text-rose-300" data-testid="remove-account-error">{error}</p>
      )}

      {!preview && !error && (
        <div className="py-8 text-center text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Reading what this account has…
        </div>
      )}

      {preview && (
        <>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
            <div className="text-white break-all">{preview.user.email}</div>
            <div className="text-gray-400 text-xs mt-1">
              {preview.user.full_name} · {preview.documents_accepted} document
              {preview.documents_accepted === 1 ? '' : 's'} accepted
              {preview.user.signs_in_with_google ? ' · signs in with Google' : ''}
            </div>
          </div>

          {preview.blocked ? (
            <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-4"
              data-testid="remove-account-blocked">
              <div className="flex gap-2.5">
                <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-100 font-medium">
                    This account is in use, so it cannot be deleted.
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-100/80">
                    {Object.entries(preview.blocking_assets).map(([k, v]) => (
                      <li key={k}>{k}: <strong>{v}</strong></li>
                    ))}
                  </ul>
                  <p className="mt-3 text-xs text-amber-100/70 leading-relaxed">
                    Archive it instead — that keeps the records and stops them signing in.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  This is what will be deleted — {preview.total_documents} records
                </div>
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-52 overflow-y-auto"
                  data-testid="remove-account-manifest">
                  {preview.will_remove.map((row) => (
                    <div key={row.collection}
                      className="flex items-center justify-between px-3.5 py-2 text-xs">
                      <span className="text-gray-300">{row.collection.replace(/_/g, ' ')}</span>
                      <span className="text-gray-500">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {preview.from_invite && (
                <label className="flex items-center gap-2.5 mt-4 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reinvite}
                    onChange={(e) => setReinvite(e.target.checked)}
                    className="w-4 h-4 accent-cyan-500"
                    data-testid="remove-account-reinvite"
                  />
                  Put their invite back to unused and email a fresh link
                </label>
              )}

              <div className="mt-4 flex gap-2.5 rounded-xl border border-rose-500/30 bg-rose-500/[0.08] p-3.5">
                <TriangleAlert className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
                <p className="text-xs text-rose-100/90 leading-relaxed">
                  There is no undo. Everything above is written to a backup file on the server
                  first, and the address is freed so they can sign up again.
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="ghost" onClick={onClose} className="text-gray-300 hover:text-white">
              Cancel
            </Button>
            {!preview.blocked && (
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-rose-600 hover:bg-rose-500 text-white"
                data-testid="remove-account-confirm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete this account'}
              </Button>
            )}
          </div>
        </>
      )}
    </Shell>
  );
};
